import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { X } from "lucide-react";
import { cn } from "@/lib/utils";

interface TutorialStep {
  id: string;
  target: string;
  title: string;
  description: string;
  position: "top" | "bottom" | "left" | "right";
}

interface InteractiveTutorialProps {
  steps: TutorialStep[];
  onComplete: () => void;
  onSkip: () => void;
}

export const InteractiveTutorial = ({ steps, onComplete, onSkip }: InteractiveTutorialProps) => {
  const [currentStep, setCurrentStep] = useState(0);
  const [tooltipPosition, setTooltipPosition] = useState({ top: 0, left: 0 });
  const [highlightPosition, setHighlightPosition] = useState({ top: 0, left: 0, width: 0, height: 0 });

  const step = steps[currentStep];
  const isLastStep = currentStep === steps.length - 1;

  useEffect(() => {
    if (!step) return;

    const targetElement = document.querySelector(step.target);
    if (!targetElement) return;

    const rect = targetElement.getBoundingClientRect();
    
    // Set highlight position
    setHighlightPosition({
      top: rect.top,
      left: rect.left,
      width: rect.width,
      height: rect.height
    });

    // Calculate tooltip position
    const tooltipOffset = 16;
    let top = 0;
    let left = 0;

    switch (step.position) {
      case "bottom":
        top = rect.bottom + tooltipOffset;
        left = rect.left + rect.width / 2;
        break;
      case "top":
        top = rect.top - tooltipOffset;
        left = rect.left + rect.width / 2;
        break;
      case "left":
        top = rect.top + rect.height / 2;
        left = rect.left - tooltipOffset;
        break;
      case "right":
        top = rect.top + rect.height / 2;
        left = rect.right + tooltipOffset;
        break;
    }

    setTooltipPosition({ top, left });
  }, [currentStep, step]);

  const handleNext = () => {
    if (isLastStep) {
      onComplete();
    } else {
      setCurrentStep(prev => prev + 1);
    }
  };

  const handlePrevious = () => {
    if (currentStep > 0) {
      setCurrentStep(prev => prev - 1);
    }
  };

  if (!step) return null;

  return (
    <div className="fixed inset-0 z-50 pointer-events-none">
      {/* Overlay with cutout */}
      <div className="absolute inset-0 bg-background/90 backdrop-blur-sm pointer-events-auto" />
      
      {/* Highlight circle/box */}
      <div
        className="absolute pointer-events-none transition-all duration-300 animate-pulse"
        style={{
          top: highlightPosition.top - 8,
          left: highlightPosition.left - 8,
          width: highlightPosition.width + 16,
          height: highlightPosition.height + 16,
          border: "4px solid hsl(var(--primary))",
          borderRadius: "12px",
          boxShadow: "0 0 0 9999px rgba(0, 0, 0, 0.5)"
        }}
      />

      {/* Tooltip */}
      <div
        className={cn(
          "absolute pointer-events-auto bg-card border border-border rounded-lg shadow-lg p-4 max-w-xs transition-all duration-300 animate-fade-in",
          step.position === "bottom" && "-translate-x-1/2",
          step.position === "top" && "-translate-x-1/2 -translate-y-full",
          step.position === "left" && "-translate-x-full -translate-y-1/2",
          step.position === "right" && "-translate-y-1/2"
        )}
        style={{
          top: tooltipPosition.top,
          left: tooltipPosition.left
        }}
      >
        {/* Close button */}
        <button
          onClick={onSkip}
          className="absolute top-2 right-2 text-muted-foreground hover:text-foreground"
        >
          <X className="w-4 h-4" />
        </button>

        {/* Content */}
        <div className="pr-6">
          <h3 className="font-semibold text-foreground mb-2">
            {step.title}
          </h3>
          <p className="text-sm text-muted-foreground mb-4">
            {step.description}
          </p>

          {/* Progress and Actions */}
          <div className="flex items-center justify-between gap-3">
            <span className="text-xs text-muted-foreground">
              Step {currentStep + 1} of {steps.length}
            </span>

            <div className="flex gap-2">
              {currentStep > 0 && (
                <Button onClick={handlePrevious} size="sm" variant="outline">
                  Back
                </Button>
              )}
              <Button onClick={handleNext} size="sm" variant="primary">
                {isLastStep ? "Done" : "Next"}
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* Skip Tutorial Button */}
      <div className="absolute bottom-8 left-1/2 -translate-x-1/2 pointer-events-auto">
        <Button onClick={onSkip} variant="ghost" size="sm">
          Skip Tutorial
        </Button>
      </div>
    </div>
  );
};
